import {
  Box,
  Button,
  Checkbox,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";

import { BankLogo } from "@dfp/react-components";

import type { BankFlowProps, BankFlowStep } from "./bankFlow.types.ts";

// Replica of Raiffeisenbank's PSD2 internet-banking login (see Confluence
// "Screen flow when connecting to a bank" → RAIFFEISEN). Copy is intentionally
// hard-coded Czech — these are external bank pages, not localized app UI.
const RB_YELLOW = "#FEE600";
const RB_DARK = "#2B2B2B";
const RB_TEXT = "#222222";
const RB_LINK = "#0066B3";
const RB_PINK = "#FCE8EA";
const RB_RED = "#D81E2C";
const RB_PAGE_BG = "#f0eee9";
const RB_INFO_BLUE = "#E7F1FB";

const FONT_STACK = '"Helvetica Neue", Helvetica, Arial, sans-serif';

function RaiffeisenHeader() {
  return (
    <Box sx={{ pt: 2, pb: 1.5 }}>
      <Stack
        direction="row"
        sx={{ alignItems: "flex-start", justifyContent: "space-between" }}
      >
        <Stack direction="row" sx={{ alignItems: "center", gap: 1.5 }}>
          <BankLogo
            bank="RAIFFEISEN"
            style={{ height: 30, width: "auto", display: "block" }}
          />
        </Stack>

        <Stack sx={{ alignItems: "flex-end", gap: 0.75 }}>
          <Stack direction="row" sx={{ alignItems: "center", gap: 2 }}>
            <Stack
              direction="row"
              sx={{ alignItems: "center", gap: 2, fontFamily: FONT_STACK }}
            >
              <Link sx={{ color: RB_LINK, fontSize: 13, cursor: "pointer" }}>
                Kontakty
              </Link>
              <Link sx={{ color: RB_LINK, fontSize: 13, cursor: "pointer" }}>
                Bezpečnost
              </Link>
              <Link sx={{ color: RB_LINK, fontSize: 13, cursor: "pointer" }}>
                Nápověda
              </Link>
            </Stack>
            <Typography
              sx={{ fontSize: 13, color: RB_TEXT, fontFamily: FONT_STACK }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>
                ✓ CZ
              </Box>
              {" | "}EN{" | "}UA
            </Typography>
          </Stack>
          <Typography
            sx={{ fontSize: 12.5, color: "#6b6b6b", fontFamily: FONT_STACK }}
          >
            Dnes je 09.06.2026{"  "}Svátek má Stanislava
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

function RaiffeisenCard({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: 1.5,
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}
    >
      {children}
    </Box>
  );
}

function RaiffeisenSafetyCard() {
  return (
    <RaiffeisenCard>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" sx={{ alignItems: "center", gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              bgcolor: RB_YELLOW,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            📞
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 18,
                color: RB_TEXT,
                fontFamily: FONT_STACK,
              }}
            >
              Volal Vám někdo?
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 14,
                color: RB_RED,
                fontFamily: FONT_STACK,
              }}
            >
              Pozor, může to být podvod
            </Typography>
          </Box>
        </Stack>

        <Typography
          sx={{
            fontSize: 13.5,
            color: RB_TEXT,
            fontFamily: FONT_STACK,
            mb: 2,
          }}
        >
          Podvodníci se rádi vydávají za Pracovníka banky z oddělení bezpečnosti
          nebo Investičního poradce.
        </Typography>

        <Stack sx={{ gap: 1.25, mb: 2 }}>
          <Stack
            direction="row"
            sx={{
              alignItems: "flex-start",
              gap: 1.25,
              bgcolor: RB_PINK,
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: RB_RED,
                mt: 0.7,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{ fontSize: 13, color: RB_TEXT, fontFamily: FONT_STACK }}
            >
              Pokud po Vás chce někdo převést peníze kvůli jejich ochraně, půjde
              o podvod. Banka napadený účet totiž vždy zablokuje.
            </Typography>
          </Stack>
          <Stack
            direction="row"
            sx={{
              alignItems: "flex-start",
              gap: 1.25,
              bgcolor: RB_PINK,
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: RB_RED,
                mt: 0.7,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{ fontSize: 13, color: RB_TEXT, fontFamily: FONT_STACK }}
            >
              Vyvarujte se také investicím do kryptoměn a akcií s neuvěřitelným
              výnosem.
            </Typography>
          </Stack>
        </Stack>

        <Typography
          sx={{
            fontSize: 13.5,
            color: RB_TEXT,
            fontFamily: FONT_STACK,
            mb: 1.5,
          }}
        >
          Pokud jste zaznamenali podobný telefonát, ihned zavěste a zavolejte na
          infolinku.
        </Typography>

        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            bgcolor: RB_YELLOW,
            borderRadius: 5,
            px: 2,
            py: 0.75,
            mb: 1.75,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: RB_TEXT,
              fontFamily: FONT_STACK,
            }}
          >
            📞 +420 412 446 402
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: RB_TEXT,
            fontFamily: FONT_STACK,
            letterSpacing: "0.02em",
          }}
        >
          MŮŽETE TOTIŽ PŘIJÍT O VŠECHNY VAŠE FINANCE.
        </Typography>
      </Box>
    </RaiffeisenCard>
  );
}

const rbInputSx = {
  bgcolor: "#fff",
  width: "100%",
  "& .MuiOutlinedInput-root": { borderRadius: 1 },
  "& input": { py: 1.1, px: 1.5, fontSize: 14, fontFamily: FONT_STACK },
};

/** Raiffeisenbank PSD2 flow: login → SCA QR authorization → account consent. */
export function RaiffeisenFlow({
  thirdPartyName,
  ownerName,
  username,
  accounts,
  initialStep,
  onConnect,
  onCancel,
}: BankFlowProps) {
  const [step, setStep] = useState<BankFlowStep>(initialStep);
  const [clientNumber, setClientNumber] = useState(username);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(accounts.map((account) => account.id)),
  );

  // The QR push approval "arrives" by itself after a moment, like the real
  // mobile-app confirmation would.
  useEffect(() => {
    if (step !== "sca") return;
    const timer = setTimeout(() => setStep("accounts"), 3000);
    return () => clearTimeout(timer);
  }, [step]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const yellowButtonSx = {
    bgcolor: RB_YELLOW,
    color: RB_TEXT,
    borderRadius: 1,
    fontFamily: FONT_STACK,
    fontWeight: 700,
    px: 3,
    boxShadow: "none",
    "&:hover": { bgcolor: "#E8D200", boxShadow: "none" },
  };

  const loginTabs: { label: string; active: boolean }[] = [
    { label: "RB klíč", active: true },
    { label: "SMS kód", active: false },
    { label: "Osobní klíč", active: false },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: RB_PAGE_BG, fontFamily: FONT_STACK }}>
      <Container maxWidth="lg">
        <RaiffeisenHeader />

        {step === "login" && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" },
              gap: 4,
              mt: 1,
              pb: 6,
            }}
          >
            <RaiffeisenCard>
              <Box sx={{ p: 3 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 22,
                    color: RB_TEXT,
                    fontFamily: FONT_STACK,
                    mb: 2,
                  }}
                >
                  Vstup na účet
                </Typography>

                <Stack
                  direction="row"
                  sx={{ gap: 3, borderBottom: "1px solid #e0e0e0", mb: 3 }}
                >
                  {loginTabs.map((tab) => (
                    <Box
                      key={tab.label}
                      sx={{
                        pb: 1,
                        borderBottom: tab.active
                          ? `3px solid ${RB_YELLOW}`
                          : "3px solid transparent",
                        cursor: "pointer",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: tab.active ? 700 : 400,
                          color: tab.active ? RB_TEXT : "#7a7a7a",
                          fontFamily: FONT_STACK,
                        }}
                      >
                        {tab.label}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: RB_TEXT,
                    fontFamily: FONT_STACK,
                    mb: 0.75,
                  }}
                >
                  Klientské číslo
                </Typography>
                <TextField
                  value={clientNumber}
                  onChange={(event) => setClientNumber(event.target.value)}
                  sx={rbInputSx}
                />

                <Box
                  sx={{
                    bgcolor: RB_INFO_BLUE,
                    borderRadius: 1,
                    p: 1.75,
                    mt: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      color: RB_TEXT,
                      fontFamily: FONT_STACK,
                      lineHeight: 1.45,
                    }}
                  >
                    Klientské číslo najdete ve vašem mobilním bankovnictví (na
                    hlavní stránce vpravo nahoře v sekci Profil tapněte na Moje
                    klientské číslo) nebo navštivte jakoukoliv pobočku.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ bgcolor: RB_DARK, p: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => setStep("sca")}
                  disabled={!clientNumber}
                  sx={yellowButtonSx}
                >
                  Přihlásit se
                </Button>
                <Typography sx={{ mt: 2 }}>
                  <Link
                    sx={{
                      color: "#cfcfcf",
                      fontSize: 13,
                      cursor: "pointer",
                      fontFamily: FONT_STACK,
                    }}
                  >
                    📶 Mobilní zařízení nemá datové připojení
                  </Link>
                </Typography>
              </Box>
            </RaiffeisenCard>

            <RaiffeisenSafetyCard />
          </Box>
        )}

        {step === "sca" && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" },
              gap: 4,
              mt: 1,
              pb: 6,
            }}
          >
            <RaiffeisenCard>
              <Box sx={{ p: 3 }}>
                <Typography sx={{ mb: 1.5 }}>
                  <Link
                    sx={{
                      color: RB_LINK,
                      fontSize: 13,
                      cursor: "pointer",
                      fontFamily: FONT_STACK,
                    }}
                  >
                    Proč musím skenovat QR kód?
                  </Link>
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 20,
                    color: RB_TEXT,
                    fontFamily: FONT_STACK,
                    mb: 2.5,
                  }}
                >
                  Bezpečnější přihlášení s QR kódem
                </Typography>

                <Stack direction="row" sx={{ gap: 3, alignItems: "center" }}>
                  <Stack sx={{ gap: 1.5, flex: 1 }}>
                    {[
                      "Pošleme vám notifikaci",
                      "Otevřete aplikaci",
                      "Naskenujte QR kód",
                      "V aplikaci potvrďte přihlášení",
                    ].map((stepLabel, index) => (
                      <Stack
                        key={stepLabel}
                        direction="row"
                        sx={{ alignItems: "center", gap: 1.5 }}
                      >
                        <Box
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            bgcolor: RB_YELLOW,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 13,
                            fontWeight: 700,
                            color: RB_TEXT,
                            fontFamily: FONT_STACK,
                            flexShrink: 0,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: RB_TEXT,
                            fontFamily: FONT_STACK,
                          }}
                        >
                          {stepLabel}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>

                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: 1,
                      border: "1px solid #d9d9d9",
                      bgcolor: "#fff",
                      backgroundImage:
                        "repeating-conic-gradient(#2B2B2B 0% 25%, #ffffff 0% 50%)",
                      backgroundSize: "16px 16px",
                      flexShrink: 0,
                    }}
                  />
                </Stack>

                <Box
                  sx={{
                    bgcolor: RB_INFO_BLUE,
                    borderRadius: 1,
                    p: 1.75,
                    mt: 2.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: RB_TEXT,
                      fontFamily: FONT_STACK,
                    }}
                  >
                    Nepřišla Vám notifikace do telefonu nebo máte potíže s
                    přihlášením?{" "}
                    <Link sx={{ color: RB_LINK, cursor: "pointer" }}>
                      Co mám udělat?
                    </Link>
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ bgcolor: RB_DARK, p: 3 }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "#e8e8e8",
                    fontFamily: FONT_STACK,
                  }}
                >
                  Přihlaste se pomocí RB klíče, pro dokončení zbývá{" "}
                  <Box
                    component="span"
                    sx={{ color: RB_YELLOW, fontWeight: 700 }}
                  >
                    04:48
                  </Box>{" "}
                  minut
                </Typography>
                <Typography sx={{ mt: 1.5 }}>
                  <Link
                    sx={{
                      color: "#cfcfcf",
                      fontSize: 13,
                      cursor: "pointer",
                      fontFamily: FONT_STACK,
                    }}
                  >
                    📶 Mobilní zařízení nemá datové připojení
                  </Link>
                </Typography>
              </Box>
            </RaiffeisenCard>

            <RaiffeisenSafetyCard />
          </Box>
        )}

        {step === "accounts" && (
          <Box sx={{ mt: 1, pb: 6, maxWidth: 720 }}>
            <RaiffeisenCard>
              <Box sx={{ p: 4 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 24,
                    color: RB_TEXT,
                    fontFamily: FONT_STACK,
                    borderBottom: `3px solid ${RB_YELLOW}`,
                    pb: 1.25,
                    mb: 2.5,
                  }}
                >
                  Povolení přístupu k účtům třetí straně
                </Typography>

                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#6b6b6b",
                    fontFamily: FONT_STACK,
                    mb: 0.5,
                  }}
                >
                  Třetí strana žádající o přístup
                </Typography>
                <Typography
                  sx={{
                    fontSize: 15,
                    color: RB_TEXT,
                    fontFamily: FONT_STACK,
                    mb: 3,
                  }}
                >
                  {thirdPartyName}
                </Typography>

                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#6b6b6b",
                    fontFamily: FONT_STACK,
                    mb: 1.25,
                  }}
                >
                  Účty, ke kterým povolím přístup
                </Typography>
                <Stack data-tour="bank-accounts" sx={{ gap: 0.5 }}>
                  {accounts.map((account) => (
                    <Stack
                      key={account.id}
                      direction="row"
                      onClick={() => toggle(account.id)}
                      sx={{ alignItems: "center", gap: 1, cursor: "pointer" }}
                    >
                      <Checkbox
                        size="small"
                        checked={selectedIds.has(account.id)}
                        sx={{
                          p: 0.5,
                          color: RB_TEXT,
                          "&.Mui-checked": { color: RB_TEXT },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: RB_TEXT,
                          fontFamily: FONT_STACK,
                        }}
                      >
                        {ownerName}{"  "}
                        {account.accountNumber}{"  "}({account.currency})
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Stack
                  direction="row"
                  sx={{ alignItems: "center", gap: 2, mt: 4 }}
                >
                  <Button
                    data-tour="bank-connect-submit"
                    variant="contained"
                    disabled={selectedIds.size === 0}
                    onClick={() =>
                      onConnect(
                        accounts.filter((account) =>
                          selectedIds.has(account.id),
                        ),
                      )
                    }
                    sx={yellowButtonSx}
                  >
                    Potvrdit
                  </Button>
                  <Button
                    variant="text"
                    onClick={onCancel}
                    sx={{
                      color: RB_TEXT,
                      fontFamily: FONT_STACK,
                      fontWeight: 700,
                      "&:hover": { bgcolor: "transparent" },
                    }}
                  >
                    Zrušit
                  </Button>
                </Stack>
              </Box>
            </RaiffeisenCard>
          </Box>
        )}
      </Container>
    </Box>
  );
}
